import { useMutation } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { CREATE_MICROFINANCE } from "apollo/mutuations";
import { customLoader } from "components/utilities/loader";

const MicrofinanceAddModal = ({ setOpened, activePage, size, refetch }) => {
  const { height } = useViewportSize();

  const [addMicrofinance, { loading: mutationLoading }] = useMutation(
    CREATE_MICROFINANCE,
    {},
  );

  const form = useForm({
    initialValues: {
      name: "",
      phone_number: "",
      max_loan_amount: null,
      interest_rate: null,
      loan_repayment_duration: 1, // Default to 1
      loan_repayment_duration_type: "MONTH", // Default selection
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name is too short" : null),
      phone_number: (value) =>
        value.length < 10 ? "Invalid phone number" : null,
      loan_repayment_duration: (value) =>
        value <= 0 ? "Duration must be greater than 0" : null,
    },
  });

  const handleNonNegativeChange =
    (field, minimum = 0) =>
    (value) => {
      form.setFieldValue(
        field,
        typeof value === "number" ? Math.max(minimum, value) : value,
      );
    };

  const preventNegativeInput = (event) => {
    if (event.key === "-") {
      event.preventDefault();
    }
  };

  const submit = (values) => {
    addMicrofinance({
      variables: {
        name: values.name,
        phone_number: values.phone_number,
        max_loan_amount: parseFloat(values.max_loan_amount),
        interest_rate: parseFloat(values.interest_rate),
        loan_repayment_duration: parseInt(values.loan_repayment_duration),
        loan_repayment_duration_type: values.loan_repayment_duration_type,
      },
      onCompleted() {
        refetch();
        showNotification({
          color: "green",
          title: "Success",
          message: "Microfinance Record Created Successfully",
        });
        setOpened(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to create record",
        });
      },
    });
  };

  return (
    <>
      <LoadingOverlay
        visible={mutationLoading}
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.2 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(submit)}>
          <Stack p="md">
            <TextInput
              required
              label="Organization Name"
              placeholder="Enter name"
              {...form.getInputProps("name")}
            />

            <TextInput
              required
              label="Phone Number"
              placeholder="+254..."
              {...form.getInputProps("phone_number")}
            />

            <Grid>
              <Grid.Col span={6}>
                <NumberInput
                  required
                  label="Max Loan Amount"
                  min={0}
                  {...form.getInputProps("max_loan_amount")}
                  onChange={handleNonNegativeChange("max_loan_amount")}
                  onKeyDown={preventNegativeInput}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  required
                  label="Interest Rate (%)"
                  min={0}
                  {...form.getInputProps("interest_rate")}
                  onChange={handleNonNegativeChange("interest_rate")}
                  onKeyDown={preventNegativeInput}
                />
              </Grid.Col>
            </Grid>

            {/* Repayment Duration Section */}
            <Grid align="flex-end">
              <Grid.Col span={6}>
                <Select
                  required
                  label="Duration Unit"
                  data={[
                    { value: "DAY", label: "Day(s)" },
                    { value: "WEEK", label: "Week(s)" },
                    { value: "MONTH", label: "Month(s)" },
                    { value: "YEAR", label: "Year(s)" },
                  ]}
                  {...form.getInputProps("loan_repayment_duration_type")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  required
                  label="Repayment Duration"
                  min={1}
                  {...form.getInputProps("loan_repayment_duration")}
                  onChange={handleNonNegativeChange(
                    "loan_repayment_duration",
                    1,
                  )}
                  onKeyDown={preventNegativeInput}
                />
              </Grid.Col>
            </Grid>

            <Button
              mt="xl"
              style={{
                width: "30%",
                backgroundColor: "#FF6A00",
                color: "#FFFFFF",
              }}
              type="submit"
            >
              Submit
            </Button>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};

export default MicrofinanceAddModal;
